import _ from "lodash";
import { useEffect, useState } from "react"
import { Button, Form, FormProps, Input, Modal, Segment, Table } from "semantic-ui-react";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { PageData } from "../models/common_model";
import { PERMISSIONS, Role, RoleConfig } from "../models/role_model";
import { fieldError } from "../utils";
import { Page } from "./commons/Page";
import "../assets/scss/components/RoleComponent.scss";
import { useCurrentUser } from "../hooks/auth-hook";


const RENAMES = {
    'equipment': 'inventory'
}

function rename(permission: string) {
    return permission.split('.').map(p => {
        if (p in RENAMES) {
            return RENAMES[p as keyof typeof RENAMES];
        } else {
            return p;
        }
    }).join('.')
}

export function RoleComponent(props: {}) {
    const currentUser = useCurrentUser();
    const [searchData, setSearchField] = useFormData<any>({});
    const [reports, setSearchData] = useFormConnect<any, PageData<Role>>({}, 'roles/search');

    const editModalControl = useModalControl();
    const viewModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    const [currentViewId, setCurrentViewId] = useState<number>();
    const [config, setConfig] = useState<RoleConfig>();

    useEffect(() => {
        restRequest<RoleConfig>('roles/config').then(setConfig)
    }, []);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Name" name="name" value={searchData['name'] || ''} onChange={setSearchField} />
                            <Input placeholder="Permissions" name="permissions" value={searchData['permissions'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <div>
                            <Button icon="search" type="submit"></Button>
                        </div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Name</th>
                            <th>Permissions</th>
                            <th>{currentUser.permissions.role.create && <Button color="blue" icon="add" content="Role" size="mini" onClick={e => {
                                setCurrentEditId(undefined);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {reports?.items.map(role => {
                            return <tr key={role.id}>
                                <td>{role.name}</td>
                                <td>{role.permissions.map(rename).join(', ')}</td>
                                <td>
                                    {currentUser.permissions.role.update && <Button onClick={e => {
                                        e.preventDefault();
                                        setCurrentEditId(role.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" compact>Edit</Button>}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={reports} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentEditId} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }} config={config}></Editor>
    </>
}

function Editor(props: { id?: number, modelControl: ModalControlData, onFinish: (data: Role) => void, config?: RoleConfig }) {
    const { id, config } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<Role>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setData({} as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<Role>('roles/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id]);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        let url: string;
        if (isCreate) {
            url = 'roles/';
        } else {
            url = `roles/${id}`
        }

        restRequest<Role>(url, data, isCreate ? 'post' : 'put').then(result => {
            props.onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }

    return <>
        <Modal {...props.modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Roles </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Group widths="equal">
                        <Form.Input required label="Name" name="name" value={data['name'] || ''} onChange={setField} error={fieldError('name', invalidError)}>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Permissions" required error={fieldError('permissions', invalidError)}>
                            {config && <PermissionView parents={[]} permissions={config?.PERMISSIONS} value={data.permissions || []} onChange={value => {
                                setField(null, { name: 'permissions', value });
                            }} />}
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}

function PermissionView(props: { parents: string[], permissions: PERMISSIONS, value: string[], onChange: (value: string[]) => void, currentTrueValues?: string[] }) {
    const { parents, permissions, value } = props;
    const items = [];
    for (let name of Object.keys(permissions)) {
        const values = permissions[name];
        const currentParents = [...parents, name];
        const currentTrueValues: string[] = [];
        items.push(<div key={currentParents.join('.')} className="item">
            <div className="title">{rename(name)} &nbsp;&nbsp;<input type="checkbox" onChange={e => {
                if (e.target.checked) {
                    props.onChange(_.uniq([...value, ...currentTrueValues]));
                } else {
                    props.onChange(value.filter(v => currentTrueValues.indexOf(v) === -1));
                }
            }} /></div>
            <div className="values">
                {values.map(itemValue => {
                    if (_.isString(itemValue)) {
                        const trueValue = [...currentParents, itemValue].join('.');
                        currentTrueValues.push(trueValue);
                        if (props.currentTrueValues) {
                            props.currentTrueValues.push(trueValue);
                        }
                        return <div key={trueValue}>{itemValue} &nbsp;&nbsp;<input checked={value.indexOf(trueValue) !== -1} onChange={e => {
                            if (e.target.checked) {
                                if (value.indexOf(trueValue) === -1) {
                                    props.onChange([...value, trueValue])
                                }
                            } else {
                                props.onChange(value.filter(v => v !== trueValue))
                            }
                        }} type="checkbox" value={trueValue} /></div>
                    } else {
                        return <PermissionView key={currentParents.join('.')} parents={currentParents} permissions={itemValue} value={value} onChange={props.onChange} currentTrueValues={currentTrueValues} />
                    }
                })}
            </div>
        </div>)
    }

    return <div className="permissions">
        {items}
    </div>
}